"use client";

import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../context/AuthContext.js";
import api from "../api/api.js";
import MainLayout from "../sidebar/Sidebar.js";
import SearchableContactDropdown from "../components/SearchableContactDropdown.js";
import AddContactPopup from "../components/AddContactPopup.js";
import { TodoActionsDropdown } from "../components/TodoActionsDropdown.js";
import SearchableProjectDropdown from "../components/SearchableProjectDropdown.js";
import {
  Search,
  Plus,
  Save,
  Trash2,
  X,
  ChevronDown,
  Tag,
  Calendar,
  Clock,
  Loader2,
} from "lucide-react";
import { useSearchInvalidation, SEARCH_QUERY_KEYS } from "../hooks/useSearchInvalidation.js";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const TodoSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col aspect-square animate-pulse">
    <div className="flex justify-between items-start mb-2">
      <div className="pr-8 flex-1">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
      </div>
      <div className="w-6 h-6 bg-gray-200 rounded"></div>
    </div>
    <div className="flex items-center mt-2">
      <div className="h-7 w-7 rounded-full bg-gray-200"></div>
      <div className="ml-2 flex-1">
        <div className="h-3 bg-gray-200 rounded w-20 mb-1"></div>
        <div className="h-2 bg-gray-200 rounded w-16"></div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2 mt-3">
      <div className="p-2 bg-gray-50 rounded-lg">
        <div className="h-2 bg-gray-200 rounded w-12 mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="p-2 bg-gray-50 rounded-lg">
        <div className="h-2 bg-gray-200 rounded w-12 mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
    <div className="mt-2">
      <div className="h-5 bg-gray-200 rounded-full w-16"></div>
    </div>
    <div className="mt-auto pt-2 border-t border-gray-100 flex justify-between items-center">
      <div className="h-3 bg-gray-200 rounded w-24"></div>
      <div className="h-6 bg-gray-200 rounded w-20"></div>
    </div>
  </div>
);

function TaskList() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateCategory } = useSearchInvalidation();

  const [location, setLocation] = useState("Tasks");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isFiltering, setIsFiltering] = useState(false);
  const [visibleTasksCount, setVisibleTasksCount] = useState(8);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [formData, setFormData] = useState({
    task_name: "",
    description: "",
    assign_to: "",
    urgency: "Medium",
    project_id: "",
    status: "Assigned",
    deadline: "",
  });
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddContactPopup, setShowAddContactPopup] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [todoToDeleteId, setTodoToDeleteId] = useState(null);
  const todosListRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newProjectData, setNewProjectData] = useState({
    project_name: "",
    project_number: "",
  });
  const [projectError, setProjectError] = useState("");
  const [showFullForm, setShowFullForm] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    const calculateVisibleTasks = () => {
      const isMobile = window.innerWidth < 640;
      const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;

      if (isMobile) {
        setVisibleTasksCount(4);
      } else if (isTablet) {
        setVisibleTasksCount(8);
      } else {
        setVisibleTasksCount(12);
      }
    };

    calculateVisibleTasks();
    window.addEventListener("resize", calculateVisibleTasks);
    return () => window.removeEventListener("resize", calculateVisibleTasks);
  }, []);

  useEffect(() => {
    setVisibleTasksCount((prev) => {
      const isMobile = window.innerWidth < 640;
      const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;

      if (isMobile) return 4;
      if (isTablet) return 8;
      return 12;
    });
  }, [statusFilter, debouncedSearchQuery]);

  const {
    data: todos = [],
    isLoading: todosLoading,
    error: todosError,
  } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.todos(user?.uid),
    queryFn: async () => {
      const response = await api.get(`/todos?user=${user}`);
      return response.data;
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.projects(),
    queryFn: async () => {
      const response = await api.get(`/projects?user=${user}`);
      return response.data;
    },
    enabled: !!user,
    staleTime: 15 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  const { data: contacts = [], isLoading: contactsLoading } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.contacts(user?.uid),
    queryFn: async () => {
      const response = await api.get(`/contacts?user=${user}`);
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  });

  const { data: contactTypes = [] } = useQuery({
    queryKey: ["contactTypes", user],
    queryFn: async () => {
      const response = await api.get(`/contacts/types/list?user=${user}`);
      return response.data;
    },
    enabled: !!user,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
  });

  const createTodoMutation = useMutation({
    mutationFn: async (todoData) => {
      const response = await api.post("/todos/", todoData);
      return response.data;
    },
    onSuccess: (newTodo) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.todos(user?.uid), (oldTodos) => [
        newTodo,
        ...oldTodos,
      ]);
      // Invalidate to ensure global search index is updated
      invalidateCategory("todos");
      setFormData({
        task_name: "",
        description: "",
        assign_to: "",
        urgency: "Medium",
        project_id: "",
        status: "Assigned",
        deadline: "",
      });
      setSelectedProject(null);
      setSelectedContact(null);
      setShowFullForm(false);

      if (newTodo.project_id) {
        queryClient.invalidateQueries({
          queryKey: ["lineItems"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ["projectTodos"],
          exact: false,
        });
      }
      if (todosListRef.current) {
        todosListRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
  });

  const updateTodoMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/todos/${id}`, data);
      return response.data;
    },
    onSuccess: (updatedTodo) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.todos(user?.uid), (oldTodos) =>
        oldTodos.map((todo) =>
          todo.id === updatedTodo.id ? updatedTodo : todo
        )
      );
      // Invalidate to ensure global search index is updated
      invalidateCategory("todos");
      if (selectedTodo && selectedTodo.id === updatedTodo.id) {
        setSelectedTodo(updatedTodo);
      }
      if (updatedTodo.project_id) {
        queryClient.invalidateQueries({
          queryKey: ["lineItems"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ["projectTodos"],
          exact: false,
        });
      }
    },
  });

  const deleteTodoMutation = useMutation({
    mutationFn: async (todoId) => {
      await api.delete(`/todos/${todoId}`);
      return todoId;
    },
    onSuccess: (deletedId) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.todos(user?.uid), (oldTodos) =>
        oldTodos.filter((todo) => todo.id !== deletedId)
      );
      // Invalidate to ensure global search index is updated
      invalidateCategory("todos");
      queryClient.invalidateQueries({
        queryKey: ["lineItems"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["projectTodos"],
        exact: false,
      });
    },
  });

  const createProjectMutation = useMutation({
    mutationFn: async (projectData) => {
      const response = await api.post("/projects/", projectData);
      return response.data;
    },
    onSuccess: (newProject) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.projects(), (oldProjects) => [
        newProject,
        ...(oldProjects || []),
      ]);
      // Invalidate to ensure global search index is updated
      invalidateCategory("projects");
      setSelectedProject(newProject);
      setFormData((prev) => ({
        ...prev,
        project_id: newProject.id,
      }));
      setShowCreateProjectModal(false);
    },
  });

  const filteredTodos = useMemo(() => {
    if (!todos.length) return [];
    if (isFiltering) return [];

    return todos.filter((todo) => {
      const matchesStatus =
        statusFilter === "All" || todo.status === statusFilter;
      const matchesSearch =
        todo.task_name
          .toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase()) ||
        todo.description
          .toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [todos, statusFilter, debouncedSearchQuery, isFiltering]);

  const visibleTodos = useMemo(() => {
    return filteredTodos.slice(0, visibleTasksCount);
  }, [filteredTodos, visibleTasksCount]);

  const loadMoreTasks = useCallback(() => {
    if (visibleTasksCount >= filteredTodos.length || isLoadingMore) return;

    setIsLoadingMore(true);

    setTimeout(() => {
      const increment =
        window.innerWidth < 640 ? 4 : window.innerWidth < 1024 ? 8 : 12;
      setVisibleTasksCount((prev) =>
        Math.min(prev + increment, filteredTodos.length)
      );
      setIsLoadingMore(false);
    }, 300);
  }, [visibleTasksCount, filteredTodos.length, isLoadingMore]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (
        scrollPercentage > 0.8 &&
        visibleTasksCount < filteredTodos.length &&
        !isLoadingMore
      ) {
        loadMoreTasks();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [loadMoreTasks, visibleTasksCount, filteredTodos.length, isLoadingMore]);

  const handleStatusFilterChange = useCallback((newStatus) => {
    setIsFiltering(true);
    setStatusFilter(newStatus);

    setTimeout(() => {
      setIsFiltering(false);
    }, 200);
  }, []);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchQuery(value);

      if (value !== debouncedSearchQuery) {
        setIsFiltering(true);
        setTimeout(() => {
          setIsFiltering(false);
        }, 350);
      }
    },
    [debouncedSearchQuery]
  );

  const handleAddProjectClick = useCallback(() => {
    setShowCreateProjectModal(true);
    setNewProjectData({ project_name: "", project_number: "" });
    setProjectError("");
  }, []);

  const handleCreateProject = useCallback(
    async (e) => {
      e.preventDefault();
      if (
        !newProjectData.project_name.trim() ||
        !newProjectData.project_number
      ) {
        setProjectError("Please fill in all fields");
        return;
      }

      try {
        setProjectError("");
        await createProjectMutation.mutateAsync({
          project_name: newProjectData.project_name.trim(),
          project_number: Number.parseInt(newProjectData.project_number, 10),
        });
      } catch (err) {
        if (err.response?.status === 409) {
          setProjectError("This project name is already in use.");
        } else if (err.response?.status === 400) {
          setProjectError(
            "Project number already exists or invalid data provided."
          );
        } else {
          setProjectError("Failed to create project. Please try again.");
        }
      }
    },
    [newProjectData, createProjectMutation]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "task_name") {
      setShowFullForm(value.length > 0);
    }
  };

  const handleInputChangeEdit = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateTask = useCallback(
    async (e) => {
      e.preventDefault();
      if (
        !formData.task_name.trim() ||
        !formData.description.trim() ||
        !selectedContact ||
        !formData.deadline
      ) {
        return;
      }

      const todoData = {
        task_name: formData.task_name.trim(),
        description: formData.description.trim(),
        assign_to: selectedContact.contact_name,
        urgency: formData.urgency,
        project_id: formData.project_id || null,
        status: formData.status,
        deadline: formData.deadline,
      };

      await createTodoMutation.mutateAsync(todoData);
    },
    [formData, selectedContact, createTodoMutation]
  );

  const handleStatusUpdate = useCallback(
    async (todoId, newStatus) => {
      await updateTodoMutation.mutateAsync({
        id: todoId,
        data: { status: newStatus },
      });
    },
    [updateTodoMutation]
  );

  const handleDeleteTodo = useCallback((todoId) => {
    setTodoToDeleteId(todoId);
    setShowDeleteConfirmModal(true);
  }, []);

  const confirmDeleteTodo = useCallback(async () => {
    if (!todoToDeleteId) return;

    try {
      await deleteTodoMutation.mutateAsync(todoToDeleteId);
      setShowDeleteConfirmModal(false);
      setTodoToDeleteId(null);
    } catch (error) {
      console.error("Failed to delete todo:", error);
    }
  }, [todoToDeleteId, deleteTodoMutation]);

  const cancelDeleteTodo = () => {
    setShowDeleteConfirmModal(false);
    setTodoToDeleteId(null);
  };

  const handleContactSelect = useCallback((contact) => {
    setSelectedContact(contact);
    setFormData((prev) => ({
      ...prev,
      assign_to: contact ? contact.contact_name : "",
    }));
  }, []);

  const handleProjectSelect = useCallback((project) => {
    setSelectedProject(project);
    setFormData((prev) => ({
      ...prev,
      project_id: project ? project.id : "",
    }));
  }, []);

  const handleAddContact = useCallback(() => {
    setShowAddContactPopup(true);
  }, []);

  const handleContactAdded = useCallback(
    (newContact) => {
      // Update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.contacts(user?.uid), (oldContacts) => [
        newContact,
        ...(oldContacts || []),
      ]);
      // Invalidate to ensure global search index is updated
      invalidateCategory("contacts");
      setSelectedContact(newContact);
      setFormData((prev) => ({
        ...prev,
        assign_to: newContact.contact_name,
      }));
    },
    [queryClient, user?.uid, invalidateCategory]
  );

  const handleEditClick = useCallback(
    (todo) => {
      setEditingTodo(todo);
      const assignedContact =
        contacts.find((c) => c.contact_name === todo.assign_to) || null;
      setSelectedContact(assignedContact);
      const assignedProject =
        projects.find((p) => p.id === todo.project_id) || null;
      setSelectedProject(assignedProject);
      setFormData({
        task_name: todo.task_name,
        description: todo.description,
        assign_to: todo.assign_to,
        urgency: todo.urgency,
        project_id: todo.project_id || "",
        status: todo.status,
        deadline: todo.deadline.split("T")[0],
      });
    },
    [contacts, projects]
  );

  const handleUpdateTodo = useCallback(
    async (e) => {
      e.preventDefault();
      if (!editingTodo) return;
      if (
        !formData.task_name.trim() ||
        !formData.description.trim() ||
        !selectedContact ||
        !formData.deadline
      ) {
        return;
      }

      const todoData = {
        task_name: formData.task_name.trim(),
        description: formData.description.trim(),
        assign_to: selectedContact.contact_name,
        urgency: formData.urgency,
        project_id: formData.project_id || null,
        status: formData.status,
        deadline: formData.deadline,
      };

      try {
        await updateTodoMutation.mutateAsync({
          id: editingTodo.id,
          data: todoData,
        });
        setEditingTodo(null);
        setFormData({
          task_name: "",
          description: "",
          assign_to: "",
          urgency: "Medium",
          project_id: "",
          status: "Assigned",
          deadline: "",
        });
        setSelectedContact(null);
        setSelectedProject(null);
      } catch (error) {
        console.error("Failed to update todo:", error);
      }
    },
    [editingTodo, formData, selectedContact, updateTodoMutation]
  );

  const handleCloseEditModal = useCallback(() => {
    setEditingTodo(null);
    setFormData({
      task_name: "",
      description: "",
      assign_to: "",
      urgency: "Medium",
      project_id: "",
      status: "Assigned",
      deadline: "",
    });
    setSelectedContact(null);
    setSelectedProject(null);
  }, []);

  const handleCancelCreate = useCallback(() => {
    setFormData({
      task_name: "",
      description: "",
      assign_to: "",
      urgency: "Medium",
      project_id: "",
      status: "Assigned",
      deadline: "",
    });
    setSelectedContact(null);
    setSelectedProject(null);
    setShowFullForm(false);
  }, []);

  const getProjectName = useCallback(
    (projectId) => {
      const project = projects.find((p) => p.id === projectId);
      return project ? project.name : "No Project";
    },
    [projects]
  );

  const getUrgencyColor = useCallback((urgency) => {
    switch (urgency) {
      case "High":
        return "bg-red-50 text-red-700 border border-red-200";
      case "Medium":
        return "bg-yellow-50 text-yellow-700 border border-yellow-200";
      case "Low":
        return "bg-gray-100 text-gray-700 border border-gray-200";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case "Assigned":
        return "border-gray-300 text-gray-600 bg-gray-50";
      case "In Progress":
        return "border-blue-300 text-blue-600 bg-blue-50";
      case "Completed":
        return "border-green-300 text-green-600 bg-green-50";
      case "Overdue":
        return "border-red-300 text-red-600 bg-red-50";
      default:
        return "border-gray-300 text-gray-600 bg-gray-50";
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    const [year, month, day] = dateString.split("-");
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  }, []);

  const handleClearFields = useCallback(() => {
    setFormData({
      description: "",
      assign_to: "",
      urgency: "Medium",
      project_id: "",
      status: "Assigned",
      deadline: "",
    });
    setSelectedContact(null);
    setSelectedProject(null);
  }, []);

  const isLoading = todosLoading || projectsLoading || contactsLoading;
  const isAnyOverlayActive =
    selectedTodo ||
    editingTodo ||
    showDeleteConfirmModal ||
    showAddContactPopup ||
    showCreateProjectModal;

  return (
    <>
      <div
        className={`relative transition duration-300 ${
          isAnyOverlayActive ? "blur-sm pointer-events-none select-none" : ""
        }`}
      >
        <MainLayout location={location} setLocation={setLocation}>
          <div className="bg-gray-50 min-h-full py-4 px-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-2">
                <h2 className="flex items-center text-xl font-semibold text-gray-900 mb-4">
                  <Plus className="h-5 w-5 mr-2 text-gray-600" />
                  Create New Task
                </h2>
                <form onSubmit={handleCreateTask} className="space-y-8">
                  <div>
                    <div>
                      <label
                        htmlFor="task_name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Task Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="task_name"
                        name="task_name"
                        value={formData.task_name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 h-11 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out"
                        placeholder="Enter task name..."
                        required
                      />
                    </div>
                  </div>
                  <AnimatePresence>
                    {showFullForm && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="space-y-6"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Assign To <span className="text-red-500">*</span>
                            </label>
                            <SearchableContactDropdown
                              contacts={contacts}
                              selectedContact={selectedContact}
                              onContactSelect={handleContactSelect}
                              onAddContact={handleAddContact}
                              placeholder="Search and select contact..."
                              className="w-full relative"
                            />
                          </div>
                          <div className="relative">
                            <label
                              htmlFor="urgency"
                              className="block text-sm font-medium text-gray-700 mb-2"
                            >
                              Urgency
                            </label>
                            <div className="relative">
                              <select
                                id="urgency"
                                name="urgency"
                                value={formData.urgency}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 h-11 pr-10 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out bg-white appearance-none"
                              >
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                              </select>
                              <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                            </div>
                          </div>
                          <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Project
                            </label>
                            <SearchableProjectDropdown
                              projects={projects}
                              selectedProject={selectedProject}
                              onProjectSelect={handleProjectSelect}
                              onAddProject={handleAddProjectClick}
                              placeholder="Search and select project..."
                              className="w-full relative"
                            />
                          </div>
                          <div className="relative">
                            <label
                              htmlFor="status"
                              className="block text-sm font-medium text-gray-700 mb-2"
                            >
                              Status
                            </label>
                            <div className="relative">
                              <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 h-11 pr-10 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out bg-white appearance-none"
                              >
                                <option value="Assigned">Assigned</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                                <option value="Overdue">Overdue</option>
                              </select>
                              <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                            </div>
                          </div>
                          <div className="relative z-10">
                            <label
                              htmlFor="deadline"
                              className="block text-sm font-medium text-gray-700 mb-2"
                            >
                              Deadline <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              id="deadline"
                              name="deadline"
                              value={formData.deadline}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2 h-11 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <div>
                            <label
                              htmlFor="description"
                              className="block text-sm font-medium text-gray-700 mb-2"
                            >
                              Description{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              id="description"
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                              rows={4}
                              className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out cursor-text"
                              placeholder="Provide a detailed description of the task..."
                              required
                            />
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                          <button
                            type="button"
                            onClick={handleClearFields}
                            className="text-sm text-gray-500 underline hover:text-gray-700 transition-colors duration-200 order-last sm:order-first"
                          >
                            Clear all fields
                          </button>
                          <div className="flex justify-center gap-4 w-full sm:w-auto">
                            <button
                              type="button"
                              onClick={handleCancelCreate}
                              className="px-6 py-3 rounded-md text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={createTodoMutation.isPending}
                              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-3 rounded-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {createTodoMutation.isPending ? (
                                <>
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                  Creating...
                                </>
                              ) : (
                                <>
                                  <Plus className="h-5 w-5" />
                                  Create Todo
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              </div>

              <div className="mb-2 flex flex-row sm:flex-row items-center justify-between gap-6 py-4 px-0">
                <div className="w-full sm:hidden relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 pr-8 text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200"
                  >
                    <option value="" disabled>
                      Select status...
                    </option>
                    {[
                      "All",
                      "Assigned",
                      "In Progress",
                      "Completed",
                      "Overdue",
                    ].map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>

                <div className="hidden sm:flex flex-wrap gap-3 justify-start">
                  {[
                    "All",
                    "Assigned",
                    "In Progress",
                    "Completed",
                    "Overdue",
                  ].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusFilterChange(status)}
                      disabled={isFiltering}
                      className={`px-5 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                        statusFilter === status
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                      } ${isFiltering ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {isFiltering && statusFilter === status && (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      )}
                      {status}
                    </button>
                  ))}
                </div>

                <div className="relative flex items-center w-full sm:w-96">
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="bg-white text-gray-900 placeholder-gray-400 rounded-lg px-4 py-2 border border-gray-200 w-full pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 ease-in-out"
                  />
                  <Search className="absolute left-3 text-gray-400 h-5 w-5" />
                  {isFiltering && searchQuery && (
                    <Loader2 className="absolute right-3 text-gray-400 h-4 w-4 animate-spin" />
                  )}
                </div>
              </div>

              {(todosError ||
                createTodoMutation.error ||
                updateTodoMutation.error ||
                deleteTodoMutation.error) && (
                <div
                  className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-8 text-center font-medium"
                  role="alert"
                >
                  {todosError?.message ||
                    createTodoMutation.error?.message ||
                    updateTodoMutation.error?.message ||
                    deleteTodoMutation.error?.message ||
                    "An error occurred"}
                </div>
              )}

              <div className="flex-1 overflow-hidden">
                <div
                  ref={scrollContainerRef}
                  className="h-full overflow-y-auto"
                  style={{ maxHeight: "calc(100vh - 200px)" }}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                          Tasks
                        </h1>
                        <p className="text-gray-500 mt-1 text-sm">
                          {filteredTodos.length} Tasks
                        </p>
                      </div>
                    </div>

                    {isLoading && (
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 w-full">
                        {Array.from({ length: 8 }).map((_, index) => (
                          <TodoSkeleton key={index} />
                        ))}
                      </div>
                    )}

                    {!isLoading &&
                      !isFiltering &&
                      filteredTodos.length === 0 && (
                        <div className="text-gray-500 text-center py-12 text-lg">
                          {searchQuery || statusFilter !== "All"
                            ? "No todos match your filters."
                            : "No todos found."}
                        </div>
                      )}

                    {isFiltering && (
                      <div className="flex items-center justify-center py-12">
                        <div className="flex items-center gap-3 text-gray-500">
                          <Loader2 className="h-6 w-6 animate-spin" />
                          <span className="text-lg font-medium">
                            Filtering tasks...
                          </span>
                        </div>
                      </div>
                    )}

                    {!isLoading && !isFiltering && visibleTodos.length > 0 && (
                      <div className="space-y-6" ref={todosListRef}>
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={`${statusFilter}-${debouncedSearchQuery}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                          >
                            {visibleTodos.map((todo, index) => (
                              <motion.div
                                key={todo.id}
                                layoutId={`todo-${todo.id}`}
                                onClick={() => setSelectedTodo(todo)}
                                className="cursor-pointer bg-white border border-gray-200 rounded-lg p-5 flex flex-col aspect-square hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                              >
                                <div className="relative flex justify-between items-start mb-2">
                                  <div className="pr-8">
                                    <h3 className="text-base font-semibold text-gray-900 leading-tight">
                                      {todo.task_name}
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                      {todo.description}
                                    </p>
                                  </div>
                                  <div
                                    className="flex-shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <TodoActionsDropdown
                                      todo={todo}
                                      onEdit={handleEditClick}
                                      onDelete={handleDeleteTodo}
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center mt-2">
                                  <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold text-gray-700">
                                    {todo.assign_to?.slice(0, 2).toUpperCase()}
                                  </div>
                                  <div className="ml-2 leading-tight">
                                    <div className="text-xs font-medium text-gray-900">
                                      {todo.assign_to}
                                    </div>
                                    <div className="text-[10px] text-gray-500">
                                      Assignee
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                                  <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                                    <div className="text-[10px] text-gray-500">
                                      Project
                                    </div>
                                    <div className="flex items-center font-medium text-gray-900">
                                      <Tag className="h-3 w-3 mr-1 text-gray-400" />
                                      <div className="truncate max-w-full">
                                        {getProjectName(todo.project_id)}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                                    <div className="text-[10px] text-gray-500">
                                      Deadline
                                    </div>
                                    <div className="flex items-center font-medium text-gray-900 truncate">
                                      <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                                      {formatDate(todo.deadline)}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className={`mt-2 inline-block px-2 py-1 text-xs font-medium rounded-md max-w-max ${getUrgencyColor(
                                    todo.urgency
                                  )}`}
                                >
                                  {todo.urgency}
                                </div>
                                <div className="mt-auto pt-2 border-t border-gray-100 flex justify-between items-center text-xs">
                                  <div className="flex items-center text-gray-500 truncate">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Created{" "}
                                    {new Date(
                                      todo.created_at
                                    ).toLocaleDateString()}
                                  </div>
                                  <select
                                    value={todo.status}
                                    onChange={(e) =>
                                      handleStatusUpdate(
                                        todo.id,
                                        e.target.value
                                      )
                                    }
                                    className={`px-2 py-1 text-xs rounded-md focus:outline-none border cursor-pointer font-medium ${getStatusColor(
                                      todo.status
                                    )}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="Assigned">Assigned</option>
                                    <option value="In Progress">
                                      In Progress
                                    </option>
                                    <option value="Completed">Completed</option>
                                    <option value="Overdue">Overdue</option>
                                  </select>
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        </AnimatePresence>

                        {visibleTasksCount < filteredTodos.length && (
                          <div className="flex flex-col items-center justify-center py-8 space-y-4">
                            {isLoadingMore ? (
                              <div className="flex items-center space-x-2 text-gray-600">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span>Loading more tasks...</span>
                              </div>
                            ) : (
                              <button
                                onClick={loadMoreTasks}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                              >
                                <span>Load More Tasks</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
          </div>
        </MainLayout>
      </div>

      <AddContactPopup
        isOpen={showAddContactPopup}
        onClose={() => setShowAddContactPopup(false)}
        onContactAdded={handleContactAdded}
        contactTypes={contactTypes}
      />

      <AnimatePresence>
        {editingTodo && (
          <motion.div
            key="editModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={handleCloseEditModal}
          >
            <motion.div
              key="editModalContent"
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg border border-gray-200 p-6 w-full max-w-lg relative"
            >
              <button
                onClick={handleCloseEditModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                aria-label="Close edit modal"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-semibold text-gray-900 mb-8">
                Edit Todo
              </h2>
              <form onSubmit={handleUpdateTodo} className="space-y-6">
                <div>
                  <label
                    htmlFor="edit_task_name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Task Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="edit_task_name"
                    name="task_name"
                    value={formData.task_name}
                    onChange={handleInputChangeEdit}
                    className="w-full px-4 py-2 h-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign To <span className="text-red-500">*</span>
                    </label>
                    <SearchableContactDropdown
                      contacts={contacts}
                      selectedContact={selectedContact}
                      onContactSelect={handleContactSelect}
                      onAddContact={handleAddContact}
                      placeholder="Search and select contact..."
                      className="w-full relative"
                    />
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="edit_urgency"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Urgency
                    </label>
                    <div className="relative">
                      <select
                        id="edit_urgency"
                        name="urgency"
                        value={formData.urgency}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 h-10 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out bg-white appearance-none"
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                      <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="edit_project_id"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Project
                    </label>
                    <select
                      id="edit_project_id"
                      name="project_id"
                      value={formData.project_id}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out bg-white"
                    >
                      <option value="">Select Project</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="relative z-10">
                    <label
                      htmlFor="edit_deadline"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Deadline <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="edit_deadline"
                      name="deadline"
                      value={formData.deadline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 h-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="edit_description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="edit_description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition duration-200 ease-in-out"
                    placeholder="Provide a detailed description of the task..."
                    required
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className="px-6 py-3 rounded-lg text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateTodoMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-3 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {updateTodoMutation.isPending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-5 w-5" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showCreateProjectModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <form
            onSubmit={handleCreateProject}
            className="bg-white p-6 rounded-lg border border-gray-200 w-96"
          >
            <h3 className="text-xl font-semibold mb-4 text-gray-900">
              Create New Project
            </h3>
            <input
              type="text"
              placeholder="Project Name"
              value={newProjectData.project_name}
              onChange={(e) =>
                setNewProjectData((prev) => ({
                  ...prev,
                  project_name: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-200 rounded mb-3 focus:outline-none focus:ring-2 focus:ring-gray-400"
              required
            />
            <input
              type="number"
              placeholder="Project Number"
              value={newProjectData.project_number}
              onChange={(e) =>
                setNewProjectData((prev) => ({
                  ...prev,
                  project_number: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-200 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-gray-400"
              required
            />
            {projectError && (
              <p className="text-red-600 mb-3 text-sm">{projectError}</p>
            )}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setShowCreateProjectModal(false)}
                className="px-4 py-2 bg-white border border-gray-200 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createProjectMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2 hover:bg-blue-700"
              >
                {createProjectMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <AnimatePresence>
        {showDeleteConfirmModal && (
          <motion.div
            key="deleteModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={cancelDeleteTodo}
          >
            <motion.div
              key="deleteModalContent"
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg border border-gray-200 p-6 w-full max-w-lg relative"
            >
              <button
                onClick={cancelDeleteTodo}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                aria-label="Close delete modal"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Confirm Deletion
              </h2>
              <p className="text-gray-600 mb-8">
                Are you sure you want to delete this todo? This action cannot be
                undone.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  type="button"
                  onClick={cancelDeleteTodo}
                  className="px-6 py-3 rounded-lg text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteTodo}
                  disabled={deleteTodoMutation.isPending}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium px-8 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
                >
                  {deleteTodoMutation.isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-5 w-5" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedTodo && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] bg-black bg-opacity-50 flex items-center justify-center p-6"
            onClick={() => setSelectedTodo(null)}
          >
            <motion.div
              className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl w-full max-h-[90vh] overflow-auto cursor-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative flex justify-between items-start">
                <div className="pr-8">
                  <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                    {selectedTodo.task_name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-2">
                    {selectedTodo.description}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <TodoActionsDropdown
                    todo={selectedTodo}
                    onEdit={(todo) => {
                      handleEditClick(todo);
                      setSelectedTodo(null);
                    }}
                    onDelete={(todo) => {
                      handleDeleteTodo(todo);
                      setSelectedTodo(null);
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center mt-4">
                <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-700">
                  {selectedTodo.assign_to?.slice(0, 2).toUpperCase()}
                </div>
                <div className="ml-3 leading-tight">
                  <div className="text-sm font-medium text-gray-900">
                    {selectedTodo.assign_to}
                  </div>
                  <div className="text-xs text-gray-500">Assignee</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Project</div>
                  <div className="flex items-center font-medium text-gray-900 truncate">
                    <Tag className="h-4 w-4 mr-1 text-gray-400" />
                    {getProjectName(selectedTodo.project_id)}
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Deadline</div>
                  <div className="flex items-center font-medium text-gray-900 truncate">
                    <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                    {formatDate(selectedTodo.deadline)}
                  </div>
                </div>
              </div>
              <div
                className={`mt-2 inline-block px-2 py-1 text-xs font-medium rounded-md max-w-max ${getUrgencyColor(
                  selectedTodo.urgency
                )}`}
              >
                {selectedTodo.urgency}
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center text-sm">
                <div className="flex items-center text-gray-500 truncate">
                  <Clock className="h-4 w-4 mr-1" />
                  Created{" "}
                  {new Date(selectedTodo.created_at).toLocaleDateString()}
                </div>
                <select
                  value={selectedTodo.status}
                  onChange={(e) =>
                    handleStatusUpdate(selectedTodo.id, e.target.value)
                  }
                  className={`px-3 py-1 text-sm rounded-md focus:outline-none border cursor-pointer font-medium ${getStatusColor(
                    selectedTodo.status
                  )}`}
                >
                  <option value="Assigned">Assigned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Overdue">Overdue</option>
                </select>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default React.memo(TaskList);

// Old Version
// import React, {
//   useEffect,
//   useState,
//   useRef,
//   useCallback,
//   useMemo,
// } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// import { useAuth } from "../context/AuthContext.js";
// import api from "../api/api.js";
// import MainLayout from "../sidebar/Sidebar.js";
// import SearchableContactDropdown from "../components/SearchableContactDropdown.js";
// import AddContactPopup from "../components/AddContactPopup.js";
// import { TodoActionsDropdown } from "../components/TodoActionsDropdown.js";
// import SearchableProjectDropdown from "../components/SearchableProjectDropdown.js";
// import {
//   Search,
//   Plus,
//   Save,
//   Trash2,
//   X,
//   ChevronDown,
//   Tag,
//   Calendar,
//   Clock,
//   Loader2,
// } from "lucide-react";

// function useDebounce(value, delay) {
//   const [debouncedValue, setDebouncedValue] = useState(value);

//   useEffect(() => {
//     const handler = setTimeout(() => {
//       setDebouncedValue(value);
//     }, delay);

//     return () => {
//       clearTimeout(handler);
//     };
//   }, [value, delay]);

//   return debouncedValue;
// }

// // Loading skeleton component
// const TodoSkeleton = () => (
//   <div className="bg-white rounded-xl shadow p-5 flex flex-col aspect-square animate-pulse">
//     <div className="flex justify-between items-start mb-2">
//       <div className="pr-8 flex-1">
//         <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
//         <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
//         <div className="h-3 bg-gray-200 rounded w-2/3"></div>
//       </div>
//       <div className="w-6 h-6 bg-gray-200 rounded"></div>
//     </div>
//     <div className="flex items-center mt-2">
//       <div className="h-7 w-7 rounded-full bg-gray-200"></div>
//       <div className="ml-2 flex-1">
//         <div className="h-3 bg-gray-200 rounded w-20 mb-1"></div>
//         <div className="h-2 bg-gray-200 rounded w-16"></div>
//       </div>
//     </div>
//     <div className="grid grid-cols-2 gap-2 mt-3">
//       <div className="p-2 bg-gray-50 rounded-lg">
//         <div className="h-2 bg-gray-200 rounded w-12 mb-1"></div>
//         <div className="h-3 bg-gray-200 rounded w-16"></div>
//       </div>
//       <div className="p-2 bg-gray-50 rounded-lg">
//         <div className="h-2 bg-gray-200 rounded w-12 mb-1"></div>
//         <div className="h-3 bg-gray-200 rounded w-20"></div>
//       </div>
//     </div>
//     <div className="mt-2">
//       <div className="h-5 bg-gray-200 rounded-full w-16"></div>
//     </div>
//     <div className="mt-auto pt-2 border-t border-gray-100 flex justify-between items-center">
//       <div className="h-3 bg-gray-200 rounded w-24"></div>
//       <div className="h-6 bg-gray-200 rounded w-20"></div>
//     </div>
//   </div>
// );

// function TaskList() {
//   const { user } = useAuth();
//   const queryClient = useQueryClient();

//   // State management
//   const [location, setLocation] = useState("Tasks");
//   const [searchQuery, setSearchQuery] = useState("");
//   const [statusFilter, setStatusFilter] = useState("All");
//   const [isFiltering, setIsFiltering] = useState(false);
//   const [visibleTasksCount, setVisibleTasksCount] = useState(8);
//   const [isLoadingMore, setIsLoadingMore] = useState(false);
//   const [formData, setFormData] = useState({
//     task_name: "",
//     description: "",
//     assign_to: "",
//     urgency: "Medium",
//     project_id: "",
//     status: "Assigned",
//     deadline: "",
//   });
//   const [selectedContact, setSelectedContact] = useState(null);
//   const [showAddContactPopup, setShowAddContactPopup] = useState(false);
//   const [editingTodo, setEditingTodo] = useState(null);
//   const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
//   const [todoToDeleteId, setTodoToDeleteId] = useState(null);
//   const todosListRef = useRef(null);
//   const scrollContainerRef = useRef(null);
//   const [selectedProject, setSelectedProject] = useState(null);
//   const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
//   const [newProjectData, setNewProjectData] = useState({
//     project_name: "",
//     project_number: "",
//   });
//   const [projectError, setProjectError] = useState("");
//   const [showFullForm, setShowFullForm] = useState(false);
//   const [selectedTodo, setSelectedTodo] = useState(null);

//   // Debounced search query
//   const debouncedSearchQuery = useDebounce(searchQuery, 300);

//   useEffect(() => {
//     const calculateVisibleTasks = () => {
//       const isMobile = window.innerWidth < 640;
//       const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;

//       if (isMobile) {
//         setVisibleTasksCount(4);
//       } else if (isTablet) {
//         setVisibleTasksCount(8);
//       } else {
//         setVisibleTasksCount(12);
//       }
//     };

//     calculateVisibleTasks();
//     window.addEventListener("resize", calculateVisibleTasks);
//     return () => window.removeEventListener("resize", calculateVisibleTasks);
//   }, []);

//   useEffect(() => {
//     setVisibleTasksCount((prev) => {
//       const isMobile = window.innerWidth < 640;
//       const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;

//       if (isMobile) return 4;
//       if (isTablet) return 8;
//       return 12;
//     });
//   }, [statusFilter, debouncedSearchQuery]);

//   // React Query hooks
//   const {
//     data: todos = [],
//     isLoading: todosLoading,
//     error: todosError,
//   } = useQuery({
//     queryKey: ["todos", user],
//     queryFn: async () => {
//       const response = await api.get(`/todos?user=${user}`);
//       return response.data;
//     },
//     enabled: !!user,
//     staleTime: 2 * 60 * 1000, // 2 min
//     cacheTime: 10 * 60 * 1000, // 10 min
//   });

//   const { data: projects = [], isLoading: projectsLoading } = useQuery({
//     queryKey: ["projects", user],
//     queryFn: async () => {
//       const response = await api.get(`/projects?user=${user}`);
//       return response.data;
//     },
//     enabled: !!user,
//     staleTime: 15 * 60 * 1000, // 15 min
//     cacheTime: 30 * 60 * 1000, // 30 min
//   });

//   const { data: contacts = [], isLoading: contactsLoading } = useQuery({
//     queryKey: ["contacts", user],
//     queryFn: async () => {
//       const response = await api.get(`/contacts?user=${user}`);
//       return response.data;
//     },
//     enabled: !!user,
//     staleTime: 5 * 60 * 1000, // 5 min
//     cacheTime: 15 * 60 * 1000, // 15 min
//   });

//   const { data: contactTypes = [] } = useQuery({
//     queryKey: ["contactTypes", user],
//     queryFn: async () => {
//       const response = await api.get(`/contacts/types/list?user=${user}`);
//       return response.data;
//     },
//     enabled: !!user,
//     staleTime: 30 * 60 * 1000, // 30 min
//     cacheTime: 60 * 60 * 1000, // 60 min
//   });

//   // Mutations for CRUD operations
//   const createTodoMutation = useMutation({
//     mutationFn: async (todoData) => {
//       const response = await api.post("/todos/", todoData);
//       return response.data;
//     },
//     onSuccess: (newTodo) => {
//       queryClient.setQueryData(["todos", user], (oldTodos) => [
//         newTodo,
//         ...oldTodos,
//       ]);
//       // Reset form
//       setFormData({
//         task_name: "",
//         description: "",
//         assign_to: "",
//         urgency: "Medium",
//         project_id: "",
//         status: "Assigned",
//         deadline: "",
//       });
//       setSelectedProject(null);
//       setSelectedContact(null);
//       setShowFullForm(false);

//       if (newTodo.project_id) {
//         queryClient.invalidateQueries({
//           queryKey: ["lineItems"],
//           exact: false,
//         });
//         queryClient.invalidateQueries({
//           queryKey: ["projectTodos"],
//           exact: false,
//         });
//       }
//       // Scroll to todos list
//       if (todosListRef.current) {
//         todosListRef.current.scrollIntoView({
//           behavior: "smooth",
//           block: "start",
//         });
//       }
//     },
//   });

//   const updateTodoMutation = useMutation({
//     mutationFn: async ({ id, data }) => {
//       const response = await api.put(`/todos/${id}`, data);
//       return response.data;
//     },
//     onSuccess: (updatedTodo) => {
//       queryClient.setQueryData(["todos", user], (oldTodos) =>
//         oldTodos.map((todo) =>
//           todo.id === updatedTodo.id ? updatedTodo : todo
//         )
//       );
//       if (selectedTodo && selectedTodo.id === updatedTodo.id) {
//         setSelectedTodo(updatedTodo);
//       }
//       if (updatedTodo.project_id) {
//         queryClient.invalidateQueries({
//           queryKey: ["lineItems"],
//           exact: false,
//         });
//         queryClient.invalidateQueries({
//           queryKey: ["projectTodos"],
//           exact: false,
//         });
//       }
//     },
//   });

//   const deleteTodoMutation = useMutation({
//     mutationFn: async (todoId) => {
//       await api.delete(`/todos/${todoId}`);
//       return todoId;
//     },
//     onSuccess: (deletedId) => {
//       queryClient.setQueryData(["todos", user], (oldTodos) =>
//         oldTodos.filter((todo) => todo.id !== deletedId)
//       );
//       queryClient.invalidateQueries({
//         queryKey: ["lineItems"],
//         exact: false,
//       });
//       queryClient.invalidateQueries({
//         queryKey: ["projectTodos"],
//         exact: false,
//       });
//     },
//   });

//   const createProjectMutation = useMutation({
//     mutationFn: async (projectData) => {
//       const response = await api.post("/projects/", projectData);
//       return response.data;
//     },
//     onSuccess: (newProject) => {
//       queryClient.setQueryData(["projects", user], (oldProjects) => [
//         newProject,
//         ...oldProjects,
//       ]);
//       setSelectedProject(newProject);
//       setFormData((prev) => ({
//         ...prev,
//         project_id: newProject.id,
//       }));
//       setShowCreateProjectModal(false);
//     },
//   });

//   // Optimized filtered todos with memoization
//   const filteredTodos = useMemo(() => {
//     if (!todos.length) return [];

//     // Show loading state briefly when filtering
//     if (isFiltering) return [];

//     return todos.filter((todo) => {
//       const matchesStatus =
//         statusFilter === "All" || todo.status === statusFilter;
//       const matchesSearch =
//         todo.task_name
//           .toLowerCase()
//           .includes(debouncedSearchQuery.toLowerCase()) ||
//         todo.description
//           .toLowerCase()
//           .includes(debouncedSearchQuery.toLowerCase());
//       return matchesStatus && matchesSearch;
//     });
//   }, [todos, statusFilter, debouncedSearchQuery, isFiltering]);

//   const visibleTodos = useMemo(() => {
//     return filteredTodos.slice(0, visibleTasksCount);
//   }, [filteredTodos, visibleTasksCount]);

//   const loadMoreTasks = useCallback(() => {
//     if (visibleTasksCount >= filteredTodos.length || isLoadingMore) return;

//     setIsLoadingMore(true);

//     // Simulate loading delay for smooth UX
//     setTimeout(() => {
//       const increment =
//         window.innerWidth < 640 ? 4 : window.innerWidth < 1024 ? 8 : 12;
//       setVisibleTasksCount((prev) =>
//         Math.min(prev + increment, filteredTodos.length)
//       );
//       setIsLoadingMore(false);
//     }, 300);
//   }, [visibleTasksCount, filteredTodos.length, isLoadingMore]);

//   useEffect(() => {
//     const scrollContainer = scrollContainerRef.current;
//     if (!scrollContainer) return;

//     const handleScroll = () => {
//       const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
//       const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

//       // Load more when user scrolls to 80% of the content
//       if (
//         scrollPercentage > 0.8 &&
//         visibleTasksCount < filteredTodos.length &&
//         !isLoadingMore
//       ) {
//         loadMoreTasks();
//       }
//     };

//     scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
//     return () => scrollContainer.removeEventListener("scroll", handleScroll);
//   }, [loadMoreTasks, visibleTasksCount, filteredTodos.length, isLoadingMore]);

//   // Handle filter changes with loading animation
//   const handleStatusFilterChange = useCallback((newStatus) => {
//     setIsFiltering(true);
//     setStatusFilter(newStatus);

//     // Brief loading animation
//     setTimeout(() => {
//       setIsFiltering(false);
//     }, 200);
//   }, []);

//   // Handle search with loading state
//   const handleSearchChange = useCallback(
//     (e) => {
//       const value = e.target.value;
//       setSearchQuery(value);

//       if (value !== debouncedSearchQuery) {
//         setIsFiltering(true);
//         // The debounced value will trigger the filter update
//         setTimeout(() => {
//           setIsFiltering(false);
//         }, 350);
//       }
//     },
//     [debouncedSearchQuery]
//   );

//   // Memoized handlers
//   const handleAddProjectClick = useCallback(() => {
//     setShowCreateProjectModal(true);
//     setNewProjectData({ project_name: "", project_number: "" });
//     setProjectError("");
//   }, []);

//   const handleCreateProject = useCallback(
//     async (e) => {
//       e.preventDefault();
//       if (
//         !newProjectData.project_name.trim() ||
//         !newProjectData.project_number
//       ) {
//         setProjectError("Please fill in all fields");
//         return;
//       }

//       try {
//         setProjectError("");
//         await createProjectMutation.mutateAsync({
//           project_name: newProjectData.project_name.trim(),
//           project_number: Number.parseInt(newProjectData.project_number, 10),
//         });
//       } catch (err) {
//         if (err.response?.status === 409) {
//           setProjectError("This project name is already in use.");
//         } else if (err.response?.status === 400) {
//           setProjectError(
//             "Project number already exists or invalid data provided."
//           );
//         } else {
//           setProjectError("Failed to create project. Please try again.");
//         }
//       }
//     },
//     [newProjectData, createProjectMutation]
//   );

//   // Handle form input changes
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//     // Show full form when user starts typing in task_name
//     if (name === "task_name") {
//       setShowFullForm(value.length > 0);
//     }
//   };

//   const handleInputChangeEdit = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   // Handle form submission
//   const handleCreateTask = useCallback(
//     async (e) => {
//       e.preventDefault();
//       if (
//         !formData.task_name.trim() ||
//         !formData.description.trim() ||
//         !selectedContact ||
//         !formData.deadline
//       ) {
//         return;
//       }

//       const todoData = {
//         task_name: formData.task_name.trim(),
//         description: formData.description.trim(),
//         assign_to: selectedContact.contact_name,
//         urgency: formData.urgency,
//         project_id: formData.project_id || null,
//         status: formData.status,
//         deadline: formData.deadline,
//       };

//       await createTodoMutation.mutateAsync(todoData);
//     },
//     [formData, selectedContact, createTodoMutation]
//   );

//   const handleStatusUpdate = useCallback(
//     async (todoId, newStatus) => {
//       await updateTodoMutation.mutateAsync({
//         id: todoId,
//         data: { status: newStatus },
//       });
//     },
//     [updateTodoMutation]
//   );

//   // --- Delete functionality (modified to use modal) ---
//   const handleDeleteTodo = useCallback((todoId) => {
//     setTodoToDeleteId(todoId);
//     setShowDeleteConfirmModal(true);
//   }, []);

//   const confirmDeleteTodo = useCallback(async () => {
//     if (!todoToDeleteId) return;

//     try {
//       await deleteTodoMutation.mutateAsync(todoToDeleteId);
//       setShowDeleteConfirmModal(false);
//       setTodoToDeleteId(null);
//     } catch (error) {
//       console.error("Failed to delete todo:", error);
//     }
//   }, [todoToDeleteId, deleteTodoMutation]);

//   const cancelDeleteTodo = () => {
//     setShowDeleteConfirmModal(false);
//     setTodoToDeleteId(null);
//   };
//   // --- End Delete functionality ---

//   // Handle contact selection from dropdown
//   const handleContactSelect = useCallback((contact) => {
//     setSelectedContact(contact);
//     setFormData((prev) => ({
//       ...prev,
//       assign_to: contact ? contact.contact_name : "",
//     }));
//   }, []);

//   const handleProjectSelect = useCallback((project) => {
//     setSelectedProject(project);
//     setFormData((prev) => ({
//       ...prev,
//       project_id: project ? project.id : "",
//     }));
//   }, []);

//   // Handle add contact popup
//   const handleAddContact = useCallback(() => {
//     setShowAddContactPopup(true);
//   }, []);

//   // Handle new contact added
//   const handleContactAdded = useCallback(
//     (newContact) => {
//       queryClient.setQueryData(["contacts", user], (oldContacts) => [
//         newContact,
//         ...oldContacts,
//       ]);
//       setSelectedContact(newContact);
//       setFormData((prev) => ({
//         ...prev,
//         assign_to: newContact.contact_name,
//       }));
//     },
//     [queryClient, user]
//   );

//   // --- Edit functionality ---
//   const handleEditClick = useCallback(
//     (todo) => {
//       setEditingTodo(todo);
//       const assignedContact =
//         contacts.find((c) => c.contact_name === todo.assign_to) || null;
//       setSelectedContact(assignedContact);
//       const assignedProject =
//         projects.find((p) => p.id === todo.project_id) || null;
//       setSelectedProject(assignedProject);
//       setFormData({
//         task_name: todo.task_name,
//         description: todo.description,
//         assign_to: todo.assign_to,
//         urgency: todo.urgency,
//         project_id: todo.project_id || "",
//         status: todo.status,
//         deadline: todo.deadline.split("T")[0],
//       });
//     },
//     [contacts, projects]
//   );

//   const handleUpdateTodo = useCallback(
//     async (e) => {
//       e.preventDefault();
//       if (!editingTodo) return;
//       if (
//         !formData.task_name.trim() ||
//         !formData.description.trim() ||
//         !selectedContact ||
//         !formData.deadline
//       ) {
//         return;
//       }

//       const todoData = {
//         task_name: formData.task_name.trim(),
//         description: formData.description.trim(),
//         assign_to: selectedContact.contact_name,
//         urgency: formData.urgency,
//         project_id: formData.project_id || null,
//         status: formData.status,
//         deadline: formData.deadline,
//       };

//       try {
//         await updateTodoMutation.mutateAsync({
//           id: editingTodo.id,
//           data: todoData,
//         });
//         setEditingTodo(null);
//         setFormData({
//           task_name: "",
//           description: "",
//           assign_to: "",
//           urgency: "Medium",
//           project_id: "",
//           status: "Assigned",
//           deadline: "",
//         });
//         setSelectedContact(null);
//         setSelectedProject(null);
//       } catch (error) {
//         console.error("Failed to update todo:", error);
//       }
//     },
//     [editingTodo, formData, selectedContact, updateTodoMutation]
//   );

//   const handleCloseEditModal = useCallback(() => {
//     setEditingTodo(null);
//     setFormData({
//       task_name: "",
//       description: "",
//       assign_to: "",
//       urgency: "Medium",
//       project_id: "",
//       status: "Assigned",
//       deadline: "",
//     });
//     setSelectedContact(null);
//     setSelectedProject(null);
//   }, []);
//   // --- End Edit functionality ---

//   // New function to handle canceling todo creation
//   const handleCancelCreate = useCallback(() => {
//     setFormData({
//       task_name: "",
//       description: "",
//       assign_to: "",
//       urgency: "Medium",
//       project_id: "",
//       status: "Assigned",
//       deadline: "",
//     });
//     setSelectedContact(null);
//     setSelectedProject(null);
//     setShowFullForm(false);
//   }, []);

//   // Get project name by ID
//   const getProjectName = useCallback(
//     (projectId) => {
//       const project = projects.find((p) => p.id === projectId);
//       return project ? project.name : "No Project";
//     },
//     [projects]
//   );

//   const getUrgencyColor = useCallback((urgency) => {
//     switch (urgency) {
//       case "High":
//         return "bg-red-100 text-red-800";
//       case "Medium":
//         return "bg-yellow-100 text-yellow-800";
//       case "Low":
//         return "bg-green-100 text-green-800";
//       default:
//         return "bg-gray-100 text-gray-800";
//     }
//   }, []);

//   const getStatusColor = useCallback((status) => {
//     switch (status) {
//       case "Assigned":
//         return "border-blue-500 text-blue-500";
//       case "In Progress":
//         return "border-yellow-500 text-yellow-500";
//       case "Completed":
//         return "border-green-500 text-green-500";
//       case "Overdue":
//         return "border-red-500 text-red-500";
//       default:
//         return "";
//     }
//   }, []);

//   // Format date string without timezone conversion
//   const formatDate = useCallback((dateString) => {
//     const [year, month, day] = dateString.split("-");
//     const date = new Date(year, month - 1, day);
//     return date.toLocaleDateString();
//   }, []);

//   const handleClearFields = useCallback(() => {
//     setFormData({
//       description: "",
//       assign_to: "",
//       urgency: "Medium",
//       project_id: "",
//       status: "Assigned",
//       deadline: "",
//     });
//     setSelectedContact(null);
//     setSelectedProject(null);
//   }, []);

//   // Loading states
//   const isLoading = todosLoading || projectsLoading || contactsLoading;
//   const isAnyOverlayActive =
//     selectedTodo ||
//     editingTodo ||
//     showDeleteConfirmModal ||
//     showAddContactPopup ||
//     showCreateProjectModal;

//   return (
//     <>
//       <div
//         className={`relative transition duration-300 ${
//           isAnyOverlayActive ? "blur-sm pointer-events-none select-none" : ""
//         }`}
//       >
//         <MainLayout location={location} setLocation={setLocation}>
//           <div className="bg-gray-50 min-h-screen py-2">
//             <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//               {/* Create Task Section */}
//               <div className="bg-white rounded-2xl shadow-sm p-6 mb-2">
//                 <h2 className="flex items-center text-2xl font-extrabold text-gray-900 mb-4 text-center">
//                   <Plus className="h-5 w-5 mr-2" />
//                   Create New Task
//                 </h2>
//                 <form onSubmit={handleCreateTask} className="space-y-8">
//                   <div>
//                     <div>
//                       <label
//                         htmlFor="task_name"
//                         className="block text-sm font-medium text-gray-700 mb-2"
//                       >
//                         Task Name <span className="text-red-500">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         id="task_name"
//                         name="task_name"
//                         value={formData.task_name}
//                         onChange={handleInputChange}
//                         className="w-full px-4 py-2 h-11 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out"
//                         placeholder="Enter task name..."
//                         required
//                       />
//                     </div>
//                   </div>
//                   <AnimatePresence>
//                     {showFullForm && (
//                       <motion.div
//                         initial={{ opacity: 0, height: 0 }}
//                         animate={{ opacity: 1, height: "auto" }}
//                         exit={{ opacity: 0, height: 0 }}
//                         transition={{ duration: 0.3, ease: "easeOut" }}
//                         className="space-y-6"
//                       >
//                         {/* ... existing form fields ... */}
//                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//                           <div className="relative">
//                             <label className="block text-sm font-medium text-gray-700 mb-2">
//                               Assign To <span className="text-red-500">*</span>
//                             </label>
//                             <SearchableContactDropdown
//                               contacts={contacts}
//                               selectedContact={selectedContact}
//                               onContactSelect={handleContactSelect}
//                               onAddContact={handleAddContact}
//                               placeholder="Search and select contact..."
//                               className="w-full relative"
//                             />
//                           </div>
//                           <div className="relative">
//                             <label
//                               htmlFor="urgency"
//                               className="block text-sm font-medium text-gray-700 mb-2"
//                             >
//                               Urgency
//                             </label>
//                             <div className="relative">
//                               <select
//                                 id="urgency"
//                                 name="urgency"
//                                 value={formData.urgency}
//                                 onChange={handleInputChange}
//                                 className="w-full px-4 py-2 h-11 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out bg-white appearance-none"
//                               >
//                                 <option value="High">High</option>
//                                 <option value="Medium">Medium</option>
//                                 <option value="Low">Low</option>
//                               </select>
//                               <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
//                             </div>
//                           </div>
//                           <div className="relative">
//                             <label className="block text-sm font-medium text-gray-700 mb-2">
//                               Project
//                             </label>
//                             <SearchableProjectDropdown
//                               projects={projects}
//                               selectedProject={selectedProject}
//                               onProjectSelect={handleProjectSelect}
//                               onAddProject={handleAddProjectClick}
//                               placeholder="Search and select project..."
//                               className="w-full relative"
//                             />
//                           </div>
//                           <div className="relative">
//                             <label
//                               htmlFor="status"
//                               className="block text-sm font-medium text-gray-700 mb-2"
//                             >
//                               Status
//                             </label>
//                             <div className="relative">
//                               <select
//                                 id="status"
//                                 name="status"
//                                 value={formData.status}
//                                 onChange={handleInputChange}
//                                 className="w-full px-4 py-2 h-11 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out bg-white appearance-none"
//                               >
//                                 <option value="Assigned">Assigned</option>
//                                 <option value="In Progress">In Progress</option>
//                                 <option value="Completed">Completed</option>
//                                 <option value="Overdue">Overdue</option>
//                               </select>
//                               <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
//                             </div>
//                           </div>
//                           <div className="relative z-10">
//                             <label
//                               htmlFor="deadline"
//                               className="block text-sm font-medium text-gray-700 mb-2"
//                             >
//                               Deadline <span className="text-red-500">*</span>
//                             </label>
//                             <input
//                               type="date"
//                               id="deadline"
//                               name="deadline"
//                               value={formData.deadline}
//                               onChange={handleInputChange}
//                               className="w-full px-4 py-2 h-11 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out"
//                               required
//                             />
//                           </div>
//                         </div>
//                         <div>
//                           <div>
//                             <label
//                               htmlFor="description"
//                               className="block text-sm font-medium text-gray-700 mb-2"
//                             >
//                               Description{" "}
//                               <span className="text-red-500">*</span>
//                             </label>
//                             <textarea
//                               id="description"
//                               name="description"
//                               value={formData.description}
//                               onChange={handleInputChange}
//                               rows={4}
//                               className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out cursor-text"
//                               placeholder="Provide a detailed description of the task..."
//                               required
//                             />
//                           </div>
//                         </div>
//                         <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
//                           <button
//                             type="button"
//                             onClick={handleClearFields}
//                             className="text-sm text-gray-500 underline hover:text-gray-700 transition-colors duration-200 order-last sm:order-first"
//                           >
//                             Clear all fields
//                           </button>
//                           <div className="flex justify-center gap-4 w-full sm:w-auto">
//                             <button
//                               type="button"
//                               onClick={handleCancelCreate}
//                               className="px-6 py-3 rounded-md text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors duration-200"
//                             >
//                               Cancel
//                             </button>
//                             <button
//                               type="submit"
//                               disabled={createTodoMutation.isPending}
//                               className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-md shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
//                             >
//                               {createTodoMutation.isPending ? (
//                                 <>
//                                   <Loader2 className="h-5 w-5 animate-spin" />
//                                   Creating...
//                                 </>
//                               ) : (
//                                 <>
//                                   <Plus className="h-5 w-5" />
//                                   Create Todo
//                                 </>
//                               )}
//                             </button>
//                           </div>
//                         </div>
//                       </motion.div>
//                     )}
//                   </AnimatePresence>
//                 </form>
//               </div>

//               {/* Filters and Search with improved loading states */}
//               <div className="mb-2 flex flex-row sm:flex-row items-center justify-between gap-6 py-4 px-0">
//                 {/* Mobile: Dropdown */}
//                 <div className="w-full sm:hidden relative">
//                   <select
//                     value={statusFilter}
//                     onChange={(e) => handleStatusFilterChange(e.target.value)}
//                     className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 pr-8 text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
//                   >
//                     <option value="" disabled>
//                       Select status...
//                     </option>
//                     {[
//                       "All",
//                       "Assigned",
//                       "In Progress",
//                       "Completed",
//                       "Overdue",
//                     ].map((status) => (
//                       <option key={status} value={status}>
//                         {status}
//                       </option>
//                     ))}
//                   </select>
//                   <svg
//                     className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
//                     fill="none"
//                     stroke="currentColor"
//                     strokeWidth="2"
//                     viewBox="0 0 24 24"
//                     xmlns="http://www.w3.org/2000/svg"
//                     aria-hidden="true"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       d="M19 9l-7 7-7-7"
//                     ></path>
//                   </svg>
//                 </div>

//                 {/* Desktop: Buttons with loading states */}
//                 <div className="hidden sm:flex flex-wrap gap-3 justify-start">
//                   {[
//                     "All",
//                     "Assigned",
//                     "In Progress",
//                     "Completed",
//                     "Overdue",
//                   ].map((status) => (
//                     <button
//                       key={status}
//                       onClick={() => handleStatusFilterChange(status)}
//                       disabled={isFiltering}
//                       className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
//                         statusFilter === status
//                           ? "bg-blue-600 text-white shadow-sm"
//                           : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
//                       } ${isFiltering ? "opacity-50 cursor-not-allowed" : ""}`}
//                     >
//                       {isFiltering && statusFilter === status && (
//                         <Loader2 className="h-3 w-3 animate-spin" />
//                       )}
//                       {status}
//                     </button>
//                   ))}
//                 </div>

//                 {/* Search bar with loading indicator */}
//                 <div className="relative flex items-center w-full sm:w-96">
//                   <input
//                     type="text"
//                     placeholder="Search tasks..."
//                     value={searchQuery}
//                     onChange={handleSearchChange}
//                     className="bg-gray-50 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-2 border border-gray-200 w-full pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out"
//                   />
//                   <Search className="absolute left-3 text-gray-400 h-5 w-5" />
//                   {isFiltering && searchQuery && (
//                     <Loader2 className="absolute right-3 text-gray-400 h-4 w-4 animate-spin" />
//                   )}
//                 </div>
//               </div>

//               {/* Error Display */}
//               {(todosError ||
//                 createTodoMutation.error ||
//                 updateTodoMutation.error ||
//                 deleteTodoMutation.error) && (
//                 <div
//                   className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8 text-center font-medium"
//                   role="alert"
//                 >
//                   {todosError?.message ||
//                     createTodoMutation.error?.message ||
//                     updateTodoMutation.error?.message ||
//                     deleteTodoMutation.error?.message ||
//                     "An error occurred"}
//                 </div>
//               )}

//               <div className="flex-1 overflow-hidden">
//                 <div
//                   ref={scrollContainerRef}
//                   className="h-full overflow-y-auto"
//                   style={{ maxHeight: "calc(100vh - 200px)" }}
//                 >
//                   <div className="p-6">
//                     {/* Header */}
//                     <div className="flex items-center justify-between mb-6">
//                       <div>
//                         <h1 className="text-2xl font-bold text-gray-900">
//                           Tasks
//                         </h1>
//                         {/* <p className="text-gray-600 mt-1">
//                           Showing {visibleTodos.length} of{" "}
//                           {filteredTodos.length} tasks
//                         </p> */}
//                         <p className="text-gray-600 mt-1">
//                           {filteredTodos.length} Tasks
//                         </p>
//                       </div>
//                     </div>

//                     {/* Loading / Empty States */}
//                     {isLoading && (
//                       <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 w-full">
//                         {Array.from({ length: 8 }).map((_, index) => (
//                           <TodoSkeleton key={index} />
//                         ))}
//                       </div>
//                     )}

//                     {!isLoading &&
//                       !isFiltering &&
//                       filteredTodos.length === 0 && (
//                         <div className="text-gray-500 text-center py-12 text-xl font-medium">
//                           {searchQuery || statusFilter !== "All"
//                             ? "No todos match your filters."
//                             : "No todos found."}
//                         </div>
//                       )}
//                     {/* Filtering loading state */}
//                     {isFiltering && (
//                       <div className="flex items-center justify-center py-12">
//                         <div className="flex items-center gap-3 text-gray-500">
//                           <Loader2 className="h-6 w-6 animate-spin" />
//                           <span className="text-lg font-medium">
//                             Filtering tasks...
//                           </span>
//                         </div>
//                       </div>
//                     )}

//                     {/* Todos List with improved animations */}
//                     {!isLoading && !isFiltering && visibleTodos.length > 0 && (
//                       <div className="space-y-6" ref={todosListRef}>
//                         <AnimatePresence mode="wait">
//                           <motion.div
//                             key={`${statusFilter}-${debouncedSearchQuery}`}
//                             initial={{ opacity: 0, y: 20 }}
//                             animate={{ opacity: 1, y: 0 }}
//                             exit={{ opacity: 0, y: -20 }}
//                             className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
//                           >
//                             {visibleTodos.map((todo, index) => (
//                               <motion.div
//                                 key={todo.id}
//                                 layoutId={`todo-${todo.id}`}
//                                 onClick={() => setSelectedTodo(todo)}
//                                 className="cursor-pointer bg-white rounded-xl shadow p-5 flex flex-col aspect-square hover:shadow-lg transition-all duration-200"
//                               >
//                                 {/* ... existing todo card content ... */}
//                                 <div className="relative flex justify-between items-start mb-2">
//                                   <div className="pr-8">
//                                     <h3 className="text-base font-semibold text-gray-900 leading-tight">
//                                       {todo.task_name}
//                                     </h3>
//                                     <p className="text-xs text-gray-600 mt-1 line-clamp-2">
//                                       {todo.description}
//                                     </p>
//                                   </div>
//                                   <div
//                                     className="flex-shrink-0"
//                                     onClick={(e) => e.stopPropagation()}
//                                   >
//                                     <TodoActionsDropdown
//                                       todo={todo}
//                                       onEdit={handleEditClick}
//                                       onDelete={handleDeleteTodo}
//                                     />
//                                   </div>
//                                 </div>
//                                 <div className="flex items-center mt-2">
//                                   <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-[10px] font-bold text-green-700">
//                                     {todo.assign_to?.slice(0, 2).toUpperCase()}
//                                   </div>
//                                   <div className="ml-2 leading-tight">
//                                     <div className="text-xs font-medium text-gray-900">
//                                       {todo.assign_to}
//                                     </div>
//                                     <div className="text-[10px] text-gray-500">
//                                       Assignee
//                                     </div>
//                                   </div>
//                                 </div>
//                                 <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
//                                   <div className="p-2 bg-gray-50 rounded-lg">
//                                     <div className="text-[10px] text-gray-500">
//                                       Project
//                                     </div>
//                                     <div className="flex items-center font-semibold text-gray-900">
//                                       <Tag className="h-3 w-3 mr-1 text-purple-500" />
//                                       <div className="truncate max-w-full">
//                                         {getProjectName(todo.project_id)}
//                                       </div>
//                                     </div>
//                                   </div>
//                                   <div className="p-2 bg-gray-50 rounded-lg">
//                                     <div className="text-[10px] text-gray-500">
//                                       Deadline
//                                     </div>
//                                     <div className="flex items-center font-semibold text-gray-900 truncate">
//                                       <Calendar className="h-3 w-3 mr-1 text-red-500" />
//                                       {formatDate(todo.deadline)}
//                                     </div>
//                                   </div>
//                                 </div>
//                                 <div
//                                   className={`mt-2 inline-block px-2 py-1 text-xs font-semibold rounded-full max-w-max ${getUrgencyColor(
//                                     todo.urgency
//                                   )}`}
//                                 >
//                                   {todo.urgency}
//                                 </div>
//                                 <div className="mt-auto pt-2 border-t border-gray-100 flex justify-between items-center text-xs">
//                                   <div className="flex items-center text-gray-500 truncate">
//                                     <Clock className="h-3 w-3 mr-1" />
//                                     Created{" "}
//                                     {new Date(
//                                       todo.created_at
//                                     ).toLocaleDateString()}
//                                   </div>
//                                   <select
//                                     value={todo.status}
//                                     onChange={(e) =>
//                                       handleStatusUpdate(
//                                         todo.id,
//                                         e.target.value
//                                       )
//                                     }
//                                     className={`px-2 py-1 text-xs rounded-full focus:outline-none border cursor-pointer ${getStatusColor(
//                                       todo.status
//                                     )}`}
//                                     onClick={(e) => e.stopPropagation()}
//                                   >
//                                     <option value="Assigned">Assigned</option>
//                                     <option value="In Progress">
//                                       In Progress
//                                     </option>
//                                     <option value="Completed">Completed</option>
//                                     <option value="Overdue">Overdue</option>
//                                   </select>
//                                 </div>
//                               </motion.div>
//                             ))}
//                           </motion.div>
//                         </AnimatePresence>

//                         {visibleTasksCount < filteredTodos.length && (
//                           <div className="flex flex-col items-center justify-center py-8 space-y-4">
//                             {isLoadingMore ? (
//                               <div className="flex items-center space-x-2 text-gray-600">
//                                 <Loader2 className="h-5 w-5 animate-spin" />
//                                 <span>Loading more tasks...</span>
//                               </div>
//                             ) : (
//                               <button
//                                 onClick={loadMoreTasks}
//                                 className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
//                               >
//                                 <span>Load More Tasks</span>
//                               </button>
//                             )}
//                           </div>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </MainLayout>
//       </div>

//       {/* ... existing modals and popups ... */}
//       <AddContactPopup
//         isOpen={showAddContactPopup}
//         onClose={() => setShowAddContactPopup(false)}
//         onContactAdded={handleContactAdded}
//         contactTypes={contactTypes}
//       />

//       {/* Edit Todo Modal */}
//       <AnimatePresence>
//         {editingTodo && (
//           <motion.div
//             key="editModalOverlay"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             transition={{ duration: 0.12, ease: "easeOut" }}
//             className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
//             onClick={handleCloseEditModal}
//           >
//             <motion.div
//               key="editModalContent"
//               initial={{ opacity: 0, y: 10, scale: 0.97 }}
//               animate={{ opacity: 1, y: 0, scale: 1 }}
//               exit={{ opacity: 0, y: 10, scale: 0.97 }}
//               onClick={(e) => e.stopPropagation()}
//               className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg relative"
//             >
//               <button
//                 onClick={handleCloseEditModal}
//                 className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
//                 aria-label="Close edit modal"
//               >
//                 <X className="h-6 w-6" />
//               </button>
//               <h2 className="text-3xl font-extrabold text-gray-900 mb-8 text-center">
//                 Edit Todo
//               </h2>
//               <form onSubmit={handleUpdateTodo} className="space-y-6">
//                 <div>
//                   <label
//                     htmlFor="edit_task_name"
//                     className="block text-sm font-medium text-gray-700 mb-2"
//                   >
//                     Task Name <span className="text-red-500">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     id="edit_task_name"
//                     name="task_name"
//                     value={formData.task_name}
//                     onChange={handleInputChangeEdit}
//                     className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
//                     required
//                   />
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="relative">
//                     {" "}
//                     {/* Removed z-10 */}
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       Assign To <span className="text-red-500">*</span>
//                     </label>
//                     <SearchableContactDropdown
//                       contacts={contacts}
//                       selectedContact={selectedContact}
//                       onContactSelect={handleContactSelect}
//                       onAddContact={handleAddContact}
//                       placeholder="Search and select contact..."
//                       className="w-full relative" // Removed z-10
//                     />
//                   </div>
//                   <div className="relative">
//                     {" "}
//                     {/* Removed z-10 */}
//                     <label
//                       htmlFor="edit_urgency"
//                       className="block text-sm font-medium text-gray-700 mb-2"
//                     >
//                       Urgency
//                     </label>
//                     <div className="relative">
//                       <select
//                         id="edit_urgency"
//                         name="urgency"
//                         value={formData.urgency}
//                         onChange={handleInputChange}
//                         className="w-full px-4 py-2 h-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out bg-white appearance-none"
//                       >
//                         <option value="High">High</option>
//                         <option value="Medium">Medium</option>
//                         <option value="Low">Low</option>
//                       </select>
//                       <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
//                     </div>
//                   </div>
//                   <div className="relative">
//                     {" "}
//                     {/* Removed z-10 */}
//                     <label
//                       htmlFor="edit_project_id"
//                       className="block text-sm font-medium text-gray-700 mb-2"
//                     >
//                       Project
//                     </label>
//                     <select
//                       id="edit_project_id"
//                       name="project_id"
//                       value={formData.project_id}
//                       onChange={handleInputChange}
//                       className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out bg-white"
//                     >
//                       <option value="">Select Project</option>
//                       {projects.map((project) => (
//                         <option key={project.id} value={project.id}>
//                           {project.name}
//                         </option>
//                       ))}
//                     </select>
//                   </div>
//                   <div className="relative z-10">
//                     <label
//                       htmlFor="edit_deadline"
//                       className="block text-sm font-medium text-gray-700 mb-2"
//                     >
//                       Deadline <span className="text-red-500">*</span>
//                     </label>
//                     <input
//                       type="date"
//                       id="edit_deadline"
//                       name="deadline"
//                       value={formData.deadline}
//                       onChange={handleInputChange}
//                       className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
//                       required
//                     />
//                   </div>
//                 </div>
//                 <div>
//                   <label
//                     htmlFor="edit_description"
//                     className="block text-sm font-medium text-gray-700 mb-2"
//                   >
//                     Description <span className="text-red-500">*</span>
//                   </label>
//                   <textarea
//                     id="edit_description"
//                     name="description"
//                     value={formData.description}
//                     onChange={handleInputChange}
//                     rows={4}
//                     className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
//                     placeholder="Provide a detailed description of the task..."
//                     required
//                   />
//                 </div>
//                 <div className="flex justify-end gap-4">
//                   <button
//                     type="button"
//                     onClick={handleCloseEditModal}
//                     className="px-6 py-3 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors duration-200"
//                   >
//                     {" "}
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     disabled={updateTodoMutation.isPending}
//                     className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
//                   >
//                     {updateTodoMutation.isPending ? (
//                       <>
//                         <Loader2 className="h-5 w-5 animate-spin" />
//                         Saving...
//                       </>
//                     ) : (
//                       <>
//                         <Save className="h-5 w-5" />
//                         Save Changes
//                       </>
//                     )}
//                   </button>
//                 </div>
//               </form>
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>

//       {/* Create Project Modal */}
//       {showCreateProjectModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <form
//             onSubmit={handleCreateProject}
//             className="bg-white p-6 rounded-md shadow-md w-96"
//           >
//             <h3 className="text-xl font-semibold mb-4">Create New Project</h3>
//             <input
//               type="text"
//               placeholder="Project Name"
//               value={newProjectData.project_name}
//               onChange={(e) =>
//                 setNewProjectData((prev) => ({
//                   ...prev,
//                   project_name: e.target.value,
//                 }))
//               }
//               className="w-full px-3 py-2 border border-gray-300 rounded mb-3"
//               required
//             />
//             <input
//               type="number"
//               placeholder="Project Number"
//               value={newProjectData.project_number}
//               onChange={(e) =>
//                 setNewProjectData((prev) => ({
//                   ...prev,
//                   project_number: e.target.value,
//                 }))
//               }
//               className="w-full px-3 py-2 border border-gray-300 rounded mb-4"
//               required
//             />
//             {projectError && (
//               <p className="text-red-600 mb-3">{projectError}</p>
//             )}
//             <div className="flex justify-end gap-4">
//               <button
//                 type="button"
//                 onClick={() => setShowCreateProjectModal(false)}
//                 className="px-4 py-2 bg-gray-300 rounded"
//               >
//                 Cancel
//               </button>
//               <button
//                 type="submit"
//                 disabled={createProjectMutation.isPending}
//                 className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2"
//               >
//                 {createProjectMutation.isPending ? (
//                   <>
//                     <Loader2 className="h-4 w-4 animate-spin" />
//                     Creating...
//                   </>
//                 ) : (
//                   "Create"
//                 )}
//               </button>
//             </div>
//           </form>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       <AnimatePresence>
//         {showDeleteConfirmModal && (
//           <motion.div
//             key="deleteModalOverlay"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             transition={{ duration: 0.12, ease: "easeOut" }}
//             className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
//             onClick={cancelDeleteTodo}
//           >
//             <motion.div
//               key="deleteModalContent"
//               initial={{ opacity: 0, y: 10, scale: 0.97 }}
//               animate={{ opacity: 1, y: 0, scale: 1 }}
//               exit={{ opacity: 0, y: 10, scale: 0.97 }}
//               onClick={(e) => e.stopPropagation()}
//               className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg relative"
//             >
//               <button
//                 onClick={cancelDeleteTodo}
//                 className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
//                 aria-label="Close delete modal"
//               >
//                 <X className="h-6 w-6" />
//               </button>
//               <h2 className="text-2xl font-bold text-gray-900 mb-4">
//                 Confirm Deletion
//               </h2>
//               <p className="text-gray-700 mb-8">
//                 Are you sure you want to delete this todo? This action cannot be
//                 undone.
//               </p>
//               <div className="flex justify-center gap-4">
//                 <button
//                   type="button"
//                   onClick={cancelDeleteTodo}
//                   className="px-6 py-3 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors duration-200"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="button"
//                   onClick={confirmDeleteTodo}
//                   disabled={deleteTodoMutation.isPending}
//                   className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
//                 >
//                   {deleteTodoMutation.isPending ? (
//                     <>
//                       <Loader2 className="h-5 w-5 animate-spin" />
//                       Deleting...
//                     </>
//                   ) : (
//                     <>
//                       <Trash2 className="h-5 w-5" />
//                       Delete
//                     </>
//                   )}
//                 </button>
//               </div>
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>

//       {/* Selected Todo Full View Modal */}
//       <AnimatePresence>
//         {selectedTodo && (
//           <motion.div
//             key="modal"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="fixed inset-0 z-[99999] bg-black bg-opacity-50 flex items-center justify-center p-6"
//             onClick={() => setSelectedTodo(null)}
//           >
//             <motion.div
//               // layoutId={`todo-${selectedTodo.id}`}
//               className="bg-white rounded-xl shadow-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-auto cursor-auto"
//               onClick={(e) => e.stopPropagation()}
//             >
//               <div className="relative flex justify-between items-start">
//                 <div className="pr-8">
//                   <h3 className="text-xl font-semibold text-gray-900 leading-tight">
//                     {selectedTodo.task_name}
//                   </h3>
//                   <p className="text-sm text-gray-700 mt-2">
//                     {selectedTodo.description}
//                   </p>
//                 </div>
//                 <div className="flex-shrink-0">
//                   <TodoActionsDropdown
//                     todo={selectedTodo}
//                     onEdit={(todo) => {
//                       handleEditClick(todo);
//                       setSelectedTodo(null);
//                     }}
//                     onDelete={(todo) => {
//                       handleDeleteTodo(todo);
//                       setSelectedTodo(null);
//                     }}
//                   />
//                 </div>
//               </div>
//               <div className="flex items-center mt-4">
//                 <div className="h-9 w-9 rounded-full bg-green-100 flex items-center justify-center text-sm font-bold text-green-700">
//                   {selectedTodo.assign_to?.slice(0, 2).toUpperCase()}
//                 </div>
//                 <div className="ml-3 leading-tight">
//                   <div className="text-sm font-medium text-gray-900">
//                     {selectedTodo.assign_to}
//                   </div>
//                   <div className="text-xs text-gray-500">Assignee</div>
//                 </div>
//               </div>
//               <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
//                 <div className="p-3 bg-gray-50 rounded-lg">
//                   <div className="text-xs text-gray-500 mb-1">Project</div>
//                   <div className="flex items-center font-semibold text-gray-900 truncate">
//                     <Tag className="h-4 w-4 mr-1 text-purple-500" />
//                     {getProjectName(selectedTodo.project_id)}
//                   </div>
//                 </div>
//                 <div className="p-3 bg-gray-50 rounded-lg">
//                   <div className="text-xs text-gray-500 mb-1">Deadline</div>
//                   <div className="flex items-center font-semibold text-black truncated">
//                     <Calendar className="h-4 w-4 mr-1 text-red-500 " />
//                     {formatDate(selectedTodo.deadline)}
//                   </div>
//                 </div>
//               </div>
//               <div
//                 className={`mt-2 inline-block px-2 py-1 text-xs font-semibold rounded-full max-w-max ${getUrgencyColor(
//                   selectedTodo.urgency
//                 )}`}
//               >
//                 {selectedTodo.urgency}
//               </div>
//               <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center text-sm">
//                 <div className="flex items-center text-gray-500 truncate">
//                   <Clock className="h-4 w-4 mr-1" />
//                   Created{" "}
//                   {new Date(selectedTodo.created_at).toLocaleDateString()}
//                 </div>
//                 <select
//                   value={selectedTodo.status}
//                   onChange={(e) =>
//                     handleStatusUpdate(selectedTodo.id, e.target.value)
//                   }
//                   className={`px-3 py-1 text-sm rounded-full focus:outline-none border cursor-pointer ${getStatusColor(
//                     selectedTodo.status
//                   )}`}
//                 >
//                   <option value="Assigned">Assigned</option>
//                   <option value="In Progress">In Progress</option>
//                   <option value="Completed">Completed</option>
//                   <option value="Overdue">Overdue</option>
//                 </select>
//               </div>
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </>
//   );
// }

// export default React.memo(TaskList);
